import * as T from 'three';
import {ALPINE_REGION,alpineBaseHeight} from './alpine-region.js';

const bounds=ALPINE_REGION.terrainBounds;
const clamp=(n,a,b)=>Math.min(b,Math.max(a,n));
const ease=t=>{const q=clamp(t,0,1);return q*q*(3-2*q);};
const key=(x,z)=>`${x.toFixed(4)},${z.toFixed(4)}`;

// Visual snow between the playable terrain and the existing distant ranges.
// The supplied material is shared: this module never owns or disposes it.
export function createAlpineBackdrop(scene,groundMaterial,ground){
  const root=new T.Group();root.name='alpine-snow-backdrop';
  const boundary=new Map();
  scene.traverse(object=>{
    if(!object.userData.terrain||!object.geometry)return;
    const p=object.geometry.attributes.position,c=object.geometry.attributes.color,n=object.geometry.attributes.normal;
    for(let i=0;i<p.count;i++){
      const x=p.getX(i),z=p.getZ(i);
      if(Math.abs(x-bounds.minX)>.001&&Math.abs(x-bounds.maxX)>.001&&Math.abs(z-bounds.minZ)>.001&&Math.abs(z-bounds.maxZ)>.001)continue;
      boundary.set(key(x,z),{x,z,y:p.getY(i),color:c?[c.getX(i),c.getY(i),c.getZ(i)]:null,normal:n?[n.getX(i),n.getY(i),n.getZ(i)]:null});
    }
  });
  const sides=[
    [...boundary.values()].filter(p=>Math.abs(p.z-bounds.minZ)<.001).sort((a,b)=>a.x-b.x),
    [...boundary.values()].filter(p=>Math.abs(p.x-bounds.maxX)<.001).sort((a,b)=>a.z-b.z),
    [...boundary.values()].filter(p=>Math.abs(p.z-bounds.maxZ)<.001).sort((a,b)=>b.x-a.x),
    [...boundary.values()].filter(p=>Math.abs(p.x-bounds.minX)<.001).sort((a,b)=>b.z-a.z),
  ];
  if(sides.some(side=>side.length<2))throw new Error('Create the near snow terrain before its backdrop.');

  const farColor=new T.Color('#dce5e9'),farRgb=farColor.toArray();
  function height(x,z){
    const nx=clamp(x,bounds.minX,bounds.maxX),nz=clamp(z,bounds.minZ,bounds.maxZ),distance=Math.hypot(x-nx,z-nz);
    const blend=ease((distance-8)/172);
    // Low snow foothills descend with the same height profile as the route.
    // Their crests overlap the side ranges, including the last kilometre.
    const rolling=7*Math.sin(x/145)*Math.cos(z/190)+5*Math.sin((x-z)/113);
    const outside=Math.max(0,Math.abs(x)-720);
    const distant=alpineBaseHeight(z)+48+rolling*.45-outside*.14;
    return blend===1?distant:ground(x,z)*(1-blend)+distant*blend;
  }
  function edgeColor(x,z){
    const nx=clamp(x,bounds.minX,bounds.maxX),nz=clamp(z,bounds.minZ,bounds.maxZ);
    const side=nz===bounds.minZ?sides[0]:nx===bounds.maxX?sides[1]:nz===bounds.maxZ?sides[2]:sides[3];
    const horizontal=side===sides[0]||side===sides[2],sign=side===sides[2]||side===sides[3]?-1:1;
    const at=p=>(horizontal?p.x:p.z)*sign,value=(horizontal?nx:nz)*sign;
    let lo=0,hi=side.length-1;while(hi-lo>1){const mid=(lo+hi)>>1;if(at(side[mid])<value)lo=mid;else hi=mid;}
    const a=side[lo],b=side[hi],t=clamp((value-at(a))/(at(b)-at(a)),0,1);
    return a.color&&b.color?a.color.map((v,i)=>v+(b.color[i]-v)*t):farColor.toArray();
  }
  const positions=[],normals=[],colors=[],uvs=[],indices=[],vertexIds=new Map();
  function vertex(x,z){
    const id=key(x,z);if(vertexIds.has(id))return vertexIds.get(id);
    const source=boundary.get(id),y=source?source.y:height(x,z),index=positions.length/3;
    positions.push(x,y,z);uvs.push(x/16,z/16);
    const dx=height(x+.5,z)-height(x-.5,z),dz=height(x,z+.5)-height(x,z-.5),length=Math.hypot(dx,1,dz);
    normals.push(...(source?.normal||[-dx/length,1/length,-dz/length]));
    const distance=Math.hypot(x-clamp(x,bounds.minX,bounds.maxX),z-clamp(z,bounds.minZ,bounds.maxZ));
    const blend=ease(distance/320),color=source?.color||edgeColor(x,z);
    colors.push(...color.map((v,i)=>v+(farRgb[i]-v)*blend));
    vertexIds.set(id,index);return index;
  }
  const rings=[sides.map(side=>side.map(p=>({index:vertex(p.x,p.z),t:0})))];
  for(const side of rings[0])for(let i=0;i<side.length;i++){
    const a=positions.slice(side[0].index*3,side[0].index*3+3),b=positions.slice(side.at(-1).index*3,side.at(-1).index*3+3),p=positions.slice(side[i].index*3,side[i].index*3+3);
    side[i].t=Math.hypot(p[0]-a[0],p[2]-a[2])/Math.hypot(b[0]-a[0],b[2]-a[2]);
  }
  // Extra rings land on the mountain base rows; a very wide triangle must
  // not cut below a foothill crest and reveal the old sky strip again.
  for(const [distance,spacing]of [[8,4],[28,8],[72,14],[140,22],[180,24],[200,24],[255,28],[320,32],[360,32],[420,36],[475,36],[520,40],[560,40],[740,44],[800,48],[1050,56]]){
    const x0=bounds.minX-distance,x1=bounds.maxX+distance,z0=bounds.minZ-distance,z1=bounds.maxZ+distance;
    const corners=[[x0,z0],[x1,z0],[x1,z1],[x0,z1]],ring=[];
    for(let side=0;side<4;side++){
      const a=corners[side],b=corners[(side+1)%4],count=Math.ceil(Math.hypot(b[0]-a[0],b[1]-a[1])/spacing),points=[];
      for(let i=0;i<=count;i++){const t=i/count;points.push({index:vertex(a[0]+(b[0]-a[0])*t,a[1]+(b[1]-a[1])*t),t});}
      ring.push(points);
    }
    rings.push(ring);
  }
  // Join unequal sample counts without T-junctions: the first ring retains
  // every one-metre boundary vertex; only distant rings become coarser.
  for(let ring=1;ring<rings.length;ring++)for(let side=0;side<4;side++){
    const a=rings[ring-1][side],b=rings[ring][side];let i=0,j=0;
    while(i<a.length-1||j<b.length-1){
      if(i<a.length-1&&(j===b.length-1||a[i+1].t<=b[j+1].t)){indices.push(a[i].index,a[i+1].index,b[j].index);i++;}
      else{indices.push(a[i].index,b[j+1].index,b[j].index);j++;}
    }
  }
  const geometry=new T.BufferGeometry();
  geometry.setAttribute('position',new T.Float32BufferAttribute(positions,3));geometry.setAttribute('normal',new T.Float32BufferAttribute(normals,3));geometry.setAttribute('color',new T.Float32BufferAttribute(colors,3));geometry.setAttribute('uv',new T.Float32BufferAttribute(uvs,2));geometry.setIndex(indices);geometry.computeBoundingSphere();
  const mesh=new T.Mesh(geometry,groundMaterial);mesh.name='snow-foothill-apron';mesh.castShadow=false;mesh.receiveShadow=false;mesh.userData.backdrop=true;root.add(mesh);scene.add(root);
  return {root,meshes:[mesh],stats:{meshes:1,vertices:positions.length/3,triangles:indices.length/3,boundaryVertices:boundary.size},dispose(){root.removeFromParent();geometry.dispose();}};
}
